import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import Spinner from "react-bootstrap/Spinner";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { getUserById, updateUser, getUserPracticeById } from "../../api";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";

export function EditUser(props) {
  const { userId, onHide, show, onUserUpdate } = props;

  const [submitting, setSubmitting] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [availablePractices, setAvailablePractices] = useState([]);
  const [loggedInRoles, setLoggedInRoles] = useState([]);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    occupation: "",
    role: [],
    practice: [],
  });

  useEffect(() => {
    async function loadUserAndPractice() {
      try {
        if (!userId) {
          console.error("No user ID provided.");
          return;
        }

        const token = sessionStorage.getItem("user");
        let availablePracs = [];
        if (token) {
          const decoded = jwtDecode(token);
          setLoggedInRoles(decoded.role);
          const loggedInUserPractices = await getUserPracticeById(decoded._id);
          availablePracs = loggedInUserPractices.map((practice) => ({
            practiceNumber: practice.practiceNumber,
            practiceName: practice.practiceName,
          }));
          setAvailablePractices(availablePracs);
        }
        
        const userData = await getUserById(userId);
        setUser(userData);
        setSelectedRoles(userData.role || []);
        setSelectedPractices(userData.practice || []);
      } catch (e) {
        console.error("Failed to load user or practices:", e);
      }
    }
    loadUserAndPractice();
  }, [userId]);

  const validateForm = () => {
    const newErrors = {};
    const isValidEmailFormat = /\S+@\S+\.\S+/.test(user.email);

    if (!user.email || !isValidEmailFormat) newErrors.email = "Enter a valid email.";
    if (!user.firstName) newErrors.firstName = "First name is required.";
    if (!user.lastName) newErrors.lastName = "Last name is required.";
    if (!user.occupation) newErrors.occupation = "Occupation is required.";
    if (!user.phoneNumber || !/^\d{3}-\d{3}-\d{4}$/.test(user.phoneNumber))
      newErrors.phoneNumber = "Enter a valid phone number (xxx-xxx-xxxx).";
    if (!selectedRoles.length) newErrors.role = "At least one role must be selected.";
    if (!selectedPractices.length) newErrors.practice = "At least one practice must be selected.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const response = await updateUser(userId, { ...user, role: selectedRoles, practice: selectedPractices });
      if (response.status === 200) {
        showToast("success", "User updated successfully.");
        props.onHide();
        onUserUpdate();
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        setErrors((prev) => ({
          ...prev,
          email: error.response.data?.error || "Email already in use.",
        }));
        return;
      }
      console.error("Failed to update user:", error);
      showToast("error", "Failed to update the user. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      const digits = value.replace(/\D/g, "");
      const formatted = digits.slice(0, 10).replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3");
      setUser((prev) => ({ ...prev, [name]: formatted }));
    } else {
      setUser((prev) => ({ ...prev, [name]: value }));
    }

    // Clear stale field-level error (e.g. the 409 email message) so the user
    // gets fresh feedback on the next submit.
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  const handleRoleChange = (role) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const handlePracticeChange = (practiceNumber) => {
    setSelectedPractices((prev) =>
      prev.includes(practiceNumber)
        ? prev.filter((p) => p !== practiceNumber)
        : [...prev, practiceNumber]
    );
  };

  function handleClose() {
    setCloseAlert(true);
  }

  const handleCancelNo = () => {
    setCloseAlert(false);
  };

  const handleCancelYes = () => {
    setCloseAlert(false);
    props.onHide();
  };

  const isSuperAdmin = loggedInRoles.includes("SuperAdmin"); // ✅ Check if the current user is a Super Admin
  const isSystemAdmin = loggedInRoles.includes("SystemAdmin"); // SystemAdmin-only features (e.g. SyncTo)

  return (
    <>
    <Modal show={show} onHide={handleClose} size="lg" scrollable centered>
      <Modal.Header
        closeButton={!closeAlert}
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Edit User
        </Modal.Title>
      </Modal.Header>
      {closeAlert && (
        <Alert
          variant="info"
          className="d-flex align-items-center justify-content-between mx-0 mb-0 border-0 rounded-0"
        >
          <span>Are you sure you want to close? Changes will not be saved.</span>
          <div className="ms-auto d-flex gap-2">
            <OverlayTrigger placement="top" overlay={<Tooltip>Discard and close</Tooltip>}>
              <Button variant="outline-primary" onClick={handleCancelYes}>
                <i className="fa-solid fa-check" />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Keep editing</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleCancelNo}>
                <i className="fa-solid fa-xmark" />
              </Button>
            </OverlayTrigger>
          </div>
        </Alert>
      )}
      <Modal.Body>
        <Form id="edit-user-form" onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="formGridEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                name="email"
                value={user.email}
                isInvalid={!!errors.email}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="userFirstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                name="firstName"
                value={user.firstName}
                isInvalid={!!errors.firstName}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.firstName}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="userLastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                name="lastName"
                value={user.lastName}
                isInvalid={!!errors.lastName}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="userHPCSA">
              <Form.Label>HPCSA#</Form.Label>
              <Form.Control
                name="hpcsa"
                value={user.hpcsa}

                onChange={handleChange}
              />
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="userPhoneNumber">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                placeholder="xxx-xxx-xxxx"
                name="phoneNumber"
                value={user.phoneNumber}
                isInvalid={!!errors.phoneNumber}
                onChange={handleChange}
              />
              <Form.Control.Feedback type="invalid">{errors.phoneNumber}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} controlId="formGridOccupation">
              <Form.Label>Occupation</Form.Label>
              <Form.Select
                name="occupation"
                value={user.occupation || ""}
                isInvalid={!!errors.occupation}
                onChange={handleChange}
              >
                <option value="">Choose...</option>
                <option>Administration</option>
                <option>Doctor</option>
                <option>Finance</option>
                <option>Nurse</option>
                <option>Occupational Therapist</option>
                <option>Podiatrist</option>
                <option>Physio Therapist</option>
                <option>Psychologist</option>
                <option>Speech Therapist</option>
                <option>Reception</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.occupation}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="roleCheckboxes">
              <Form.Label>Roles</Form.Label>
              <div>
                {["Administration", "Practitioner", "Finance", "SuperAdmin"].map((role) => (
                  <Form.Check
                    key={role}
                    type="checkbox"
                    label={role}
                    checked={selectedRoles.includes(role)}
                    onChange={isSuperAdmin ? () => handleRoleChange(role) : undefined} // Disable interaction for non-admins
                    disabled={!isSuperAdmin}
                  />
                ))}
              </div>
              {errors.role && <div className="text-danger small">{errors.role}</div>}
            </Form.Group>

            <Form.Group as={Col} controlId="practiceCheckboxes">
              <Form.Label>Practices</Form.Label>
              <div>
                {availablePractices.map((prac) => (
                  <Form.Check
                    key={prac.practiceNumber}
                    type="checkbox"
                    label={prac.practiceName}
                    checked={selectedPractices.includes(prac.practiceNumber)}
                    onChange={isSuperAdmin ? () => handlePracticeChange(prac.practiceNumber) : undefined} // Disable interaction for non-admins
                    disabled={!isSuperAdmin}
                  />
                ))}
              </div>
              {errors.practice && <div className="text-danger small">{errors.practice}</div>}
            </Form.Group>

            {/* SystemAdmin-only: enable SyncTo (Azure transcription) and the
                AI Patient History panel for this user. Defence-in-depth —
                backend allowlist also requires SystemAdmin to persist these. */}
            {isSystemAdmin && (
              <Form.Group as={Col} controlId="featuresSyncTo">
                <Form.Label>Features</Form.Label>
                <div>
                  <Form.Check
                    type="checkbox"
                    id="feature-syncTo"
                    label="SyncTo (Azure transcription)"
                    checked={!!user?.features?.syncTo}
                    onChange={(e) =>
                      setUser((prev) => ({
                        ...prev,
                        features: {
                          ...(prev.features || {}),
                          syncTo: e.target.checked,
                        },
                      }))
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="feature-AIHistory"
                    label="AI Patient History"
                    checked={!!user?.features?.AIHistory}
                    onChange={(e) =>
                      setUser((prev) => ({
                        ...prev,
                        features: {
                          ...(prev.features || {}),
                          AIHistory: e.target.checked,
                        },
                      }))
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    id="feature-tasks"
                    label="Tasks"
                    checked={!!user?.features?.tasks}
                    onChange={(e) =>
                      setUser((prev) => ({
                        ...prev,
                        features: {
                          ...(prev.features || {}),
                          tasks: e.target.checked,
                        },
                      }))
                    }
                  />
                </div>
              </Form.Group>
            )}
          </Row>

        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          type="submit"
          form="edit-user-form"
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          {submitting ? <Spinner size="sm" animation="border" /> : "Save"}
        </Button>
        <Button variant="outline-secondary" onClick={handleClose} style={{ minWidth: 140 }}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
    <AppToast
      show={toast.show}
      onClose={hideToast}
      variant={toast.variant}
      message={toast.message}
    />
    </>
  );
}
